import { LAST_XP_REASON, XpReason } from "../rules/levels.ts";
import { HEADER_BYTES, MALFORMED, OK, assertField, checkHeader, putHeader, putU16, putU32, readU16, readU32, type DecodeVerdict } from "./binary.ts";
import { MessageType } from "./catalogue.ts";
import { MAX_U16, MAX_U32 } from "./limits.ts";

/**
 * The `progress` frame (ADR-083 decision 6, PROTOCOL.md 5.14): server to client, a character's own level and XP, 11
 * bytes: the header, `level` `u8` (1 to 255), `xp` `u32` (the total), `gained` `u16` (the XP the tick added, 0 in the
 * frame after `welcome`) and `reason` `u8` (`XpReason`). The page reads the thresholds from its own rules tables. The
 * encoder writes into a caller-owned buffer, so a send allocates nothing; the decoder never throws (ADR-030).
 */

/**
 * Total `progress` frame length.
 */
export const PROGRESS_BYTES = 11;

const MAX_LEVEL = 0xff;

/**
 * A decoded or to-be-encoded `progress`.
 */
export interface ProgressRecord {
  level: number;
  xp: number;
  gained: number;
  reason: number;
}

/**
 * A fresh record for `decodeProgress` to fill.
 *
 * @returns Level 1, no XP, nothing gained, no reason.
 */
export function newProgressRecord(): ProgressRecord {
  return { level: 1, xp: 0, gained: 0, reason: XpReason.None };
}

/**
 * Encodes a `progress` into a caller-owned buffer.
 *
 * @param buffer Destination, at least `PROGRESS_BYTES` long.
 * @param record The values.
 * @returns Bytes written, always `PROGRESS_BYTES`.
 * @throws Error when the buffer is too small, the level is outside 1 to 255, the XP is not a u32, the gain not a u16
 *   or the reason not one this build numbers.
 */
export function encodeProgress(buffer: Uint8Array, record: Readonly<ProgressRecord>): number {
  if (buffer.length < PROGRESS_BYTES) {
    throw new Error(`buffer of ${buffer.length} bytes cannot hold a ${PROGRESS_BYTES}-byte progress`);
  }
  if (!Number.isInteger(record.level) || record.level < 1 || record.level > MAX_LEVEL) {
    throw new Error(`progress level ${record.level} does not fit in 1 to ${MAX_LEVEL}`);
  }
  assertField(record.xp, MAX_U32, `progress xp ${record.xp}`);
  assertField(record.gained, MAX_U16, `progress gained ${record.gained}`);
  assertField(record.reason, LAST_XP_REASON, `progress reason ${record.reason}`);
  putHeader(buffer, MessageType.Progress);
  buffer[HEADER_BYTES] = record.level;
  putU32(buffer, HEADER_BYTES + 1, record.xp);
  putU16(buffer, HEADER_BYTES + 5, record.gained);
  buffer[HEADER_BYTES + 7] = record.reason;
  return PROGRESS_BYTES;
}

/**
 * Decodes a `progress` frame into a record. Never throws: a wrong length or type, or a level of 0, is malformed and a
 * wrong version a version mismatch, leaving the record untouched; a reason this build does not number reads as none.
 *
 * @param frame The frame.
 * @param out The record to fill.
 * @returns The verdict.
 */
export function decodeProgress(frame: Uint8Array, out: ProgressRecord): DecodeVerdict {
  const header = checkHeader(frame, MessageType.Progress, PROGRESS_BYTES);
  if (header.kind !== "ok") {
    return header;
  }
  const level = frame[HEADER_BYTES] as number;
  if (level === 0) {
    return MALFORMED;
  }
  const reason = frame[HEADER_BYTES + 7] as number;
  out.level = level;
  out.xp = readU32(frame, HEADER_BYTES + 1);
  out.gained = readU16(frame, HEADER_BYTES + 5);
  out.reason = reason <= LAST_XP_REASON ? reason : XpReason.None;
  return OK;
}
